import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  differenceBy,
  filter,
  find,
  forEach,
  isEmpty,
  map,
  sortBy,
  trim,
  uniq,
} from "lodash/fp";

import apiClient from "@app/common/api/apiClient";
import type {
  CreateSessionTagBody,
  SessionInfo,
  Tag,
  UserMeeting,
} from "@app/common/types";
import { replaceWhere } from "@app/utils/arrays";

interface UpdateTagsBody {
  updatedTags: Tag[];
  applyToSeries?: boolean;
}

const useSessionTags = ({
  sessionId,
  sessionTags,
  context,
}: {
  sessionId: string;
  sessionTags: Tag[];
  context?: "sessionsList" | "sessionInfo";
}) => {
  const queryClient = useQueryClient();

  const updateTagsInSessionsList = ({
    applyToSeries = false,
    sessions,
    sessionId,
    tags,
  }: {
    applyToSeries?: boolean;
    sessions: UserMeeting[];
    sessionId: string;
    tags: Tag[];
  }) => {
    if (isEmpty(sessions)) return sessions;

    const previousSession = find({ id: sessionId }, sessions);
    if (!previousSession) return sessions;

    let newQueryData = sessions;

    if (applyToSeries && previousSession.seriesId) {
      // optimistically update all meetings in the series
      const allSessionToUpdate = filter(
        { seriesId: previousSession.seriesId },
        sessions
      );
      forEach((session) => {
        const prevSession = find({ id: session.id }, sessions);
        if (prevSession) {
          const updatedSession = { ...prevSession, tags };
          newQueryData = replaceWhere(
            newQueryData,
            updatedSession,
            ({ id }) => id === session.id
          );
        }
      }, allSessionToUpdate);
    } else {
      const updatedSession = { ...previousSession, tags };
      newQueryData = replaceWhere(
        sessions,
        updatedSession,
        ({ id }) => id === sessionId
      );
    }

    return newQueryData;
  };

  const updateTagsMutation = useMutation({
    mutationFn: async ({ updatedTags, applyToSeries }: UpdateTagsBody) => {
      if (!sessionId) return;

      const tagsToCreate = differenceBy("id", updatedTags, sessionTags);
      const tagsToRemove = differenceBy("id", sessionTags, updatedTags);

      const results = await Promise.allSettled([
        // removal calls
        ...map(
          (tag) =>
            apiClient.session.tags.removeSessionTag(
              sessionId,
              tag.id,
              applyToSeries
            ),
          tagsToRemove
        ),
        // create calls
        ...map((tag) => {
          const body: CreateSessionTagBody = {
            name: trim(tag.name),
            color: tag.color,
            workspaceId: tag.workspaceId,
            applyToSeries,
          };

          return apiClient.session.tags.createSessionTag(sessionId, body);
        }, tagsToCreate),
      ]);

      const hasErrors = uniq(map("status", results)).includes("rejected");

      if (hasErrors) {
        // throw error so that we can react to any one of the create/remove calls failing
        throw new Error("Error updating session tags");
      }
    },
    onSettled: () => {
      // refresh user and session tag lists
      queryClient.invalidateQueries({ queryKey: [context] });
      queryClient.invalidateQueries({ queryKey: ["tags"] });
    },
    onMutate: async ({ updatedTags, applyToSeries }) => {
      const sortedTags = sortBy("name", updatedTags);

      if (context === "sessionInfo") {
        // optimistically update meeting info on MR page
        const singleSessionInfoQueryKey = ["sessionInfo", sessionId];
        await queryClient.cancelQueries({
          queryKey: singleSessionInfoQueryKey,
        });
        const prevMeetingInfo = queryClient.getQueryData(
          singleSessionInfoQueryKey
        ) as SessionInfo;

        const newMeetingInfo = {
          ...prevMeetingInfo,
          tags: sortedTags,
        };
        queryClient.setQueryData(singleSessionInfoQueryKey, newMeetingInfo);
        return;
      }

      if (context === "sessionsList") {
        // optimistically update session(s) in session list on all tabs
        const queryKey = ["sessionsList"];
        await queryClient.cancelQueries({ queryKey });

        const queriesData = queryClient.getQueriesData<UserMeeting[]>({
          queryKey,
          exact: false,
        });

        forEach(([queryKey, queryData]) => {
          const updatedData = updateTagsInSessionsList({
            applyToSeries,
            sessions: queryData ?? [],
            sessionId,
            tags: sortedTags,
          });
          queryClient.setQueryData(queryKey, updatedData);
        }, queriesData);
      }
    },
  });

  return {
    updateSessionTags: updateTagsMutation,
  };
};

export default useSessionTags;
